// AttachmentGallery — thumbnail strip + lightbox for Dev Tracker item images.
//
// The parent wraps its whole card with `useAttachmentPaste(onFile)` so a user
// can Ctrl/Cmd-V or drag-drop an image anywhere on the card and it will upload
// automatically. This component only renders the resulting gallery + lightbox.
//
// Auth: attachments are served through `/api/attachments/{path}` with a
// short-lived query-param JWT so raw <img src> tags work. Token comes from
// `localStorage.talmia_token` (set by the axios interceptor on login).

import { useEffect, useState } from "react";
import api from "../../lib/api";
import { X, Trash2, Image as ImageIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export function attachmentUrl(path) {
  const token = typeof window !== "undefined" ? localStorage.getItem("talmia_token") : "";
  return `${API}/api/attachments/${path}?auth=${encodeURIComponent(token || "")}`;
}

export default function AttachmentGallery({ itemId, attachments = [], onChange }) {
  const [preview, setPreview] = useState(null); // active lightbox path
  const [deleting, setDeleting] = useState(null);

  const remove = async (att) => {
    if (deleting) return;
    setDeleting(att.id);
    try {
      await api.delete(`/items/${itemId}/attachments/${att.id}`);
      onChange && onChange(attachments.filter((a) => a.id !== att.id));
    } finally { setDeleting(null); }
  };

  // Close lightbox on Escape
  useEffect(() => {
    if (!preview) return;
    const h = (e) => { if (e.key === "Escape") setPreview(null); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [preview]);

  return (
    <div data-testid="attachment-gallery">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
          <ImageIcon className="w-3.5 h-3.5" /> Attachments
          <span className="text-[10px] text-slate-500">({attachments.length})</span>
        </h4>
        <span className="text-[10px] text-slate-500 font-mono">Ctrl/Cmd-V or drag to add</span>
      </div>

      {attachments.length === 0 ? (
        <div className="text-[11px] text-slate-500 py-3 border border-dashed border-white/10 rounded-md text-center">
          No images yet. Paste a screenshot anywhere in this card.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="relative group w-24 h-24 rounded-md overflow-hidden border border-white/10 bg-slate-900"
              title={`${att.filename} · ${att.added_by_name || "?"} · ${new Date(att.added_at).toLocaleString()}`}
              data-testid={`attachment-${att.id}`}
            >
              <img
                src={attachmentUrl(att.path)}
                alt={att.filename}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => setPreview(att)}
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => remove(att)}
                disabled={deleting === att.id}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 border border-white/10 opacity-0 group-hover:opacity-100 grid place-items-center text-rose-300 hover:text-rose-200 hover:bg-black transition"
                data-testid={`attachment-delete-${att.id}`}
                aria-label="Delete attachment"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm grid place-items-center p-6"
          onClick={() => setPreview(null)}
          data-testid="attachment-lightbox"
        >
          <button
            type="button"
            onClick={() => setPreview(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"
            data-testid="attachment-lightbox-close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <img
            src={attachmentUrl(preview.path)}
            alt={preview.filename}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[11px] font-mono text-slate-300 bg-black/60 px-3 py-1.5 rounded-full">
            {preview.filename} · {preview.added_by_name || "?"} · {new Date(preview.added_at).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Paste/drop hook — attach to any card ref ───────────────────────
// Consumer: const dropProps = useAttachmentPaste(uploadFile);  <div {...dropProps}>...
// `uploadFile(File)` is called for every image found in the paste/drop.
export function useAttachmentPaste(uploadFile) {
  const [dragActive, setDragActive] = useState(false);

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) uploadFile(f);
      }
    }
  };
  const handleDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    files.filter((f) => f.type.startsWith("image/")).forEach(uploadFile);
  };
  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragActive(false); };

  return {
    onPaste: handlePaste,
    onDrop: handleDrop,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    // Consumer applies this via className / style to show a drop hint.
    _dragActive: dragActive,
  };
}
